import {UnitPriceChoice} from "@/components/docs/UnitPriceChoice";
/** Purchase-order lines with captured purchase units and server-validated PI provenance. */
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PoLineDto, PoLineInput, ProductSummaryDto } from "@hisense/shared";
import { describe, type DescriptionLang } from "@hisense/shared";
import { lineAmountMinor, totalMinor } from "@hisense/shared";
import { formatMoney, formatQty, unitPriceToInput } from "@/domain/money";
import { parseIntField, parseMoneyMajor } from "@/lib/moneyInput";
import { PoProductPicker } from "@/components/purchaseOrders/PoProductPicker";
import { useActiveUnitProducts } from "@/api/unitPricing";
import { PO_COLUMN_WIDTH, PO_DESCRIPTION_MIN_REM, PO_EDGE_COLUMNS_REM, PO_MAIN_COLUMNS, PO_SIDE_COLUMNS, type PoColumn } from "./poFormLayout";

export interface PoLineDraft {
  id?:string;
  unitSelection?:import("@hisense/shared").UnitSelection;
  productId: string;
  productCode: string;
  subRef: string;
  lotNumber: string;
  description: string;
  /** Bản tiếng Hoa, chụp cùng lúc với bản tiếng Anh. */
  descriptionZh: string;
  color: string;
  size: string;
  moq: string;
  quantity: string;
  unit: string;
  piItemId: string | null;
  /** Major units, exactly as typed — parsed (never coerced) on save. */
  unitPrice: string;
  remarks: string;
  supplierName: string;
}

export function blankPoLine(): PoLineDraft {
  return {
    productId: "", productCode: "", subRef: "", lotNumber: "", description: "", descriptionZh: "", color: "", size: "",
    moq: "", quantity: "1000", unit: "", piItemId: null, unitPrice: "", remarks: "", supplierName: "",
  };
}

/**
 * Prefill from a catalog product at the supplier COST price — a purchase order
 * buys, it does not sell, so sellingPrice must never leak in here. Quantity
 * defaults to 1000, matching legacy handleSelectSupplierFromPI.
 */
export function poLineFromProduct(p: ProductSummaryDto, supplierName: string | null, currency?: string): PoLineDraft {
  return {
    productId: p.id,
    productCode: p.productCode,
    subRef: p.subRef ?? "",
    lotNumber: "",
    description: p.description ?? "", descriptionZh: p.descriptionZh ?? "",
    color: p.color ?? "",
    size: p.size ?? "",
    moq: p.moq === null ? "" : String(p.moq),
    quantity: "1000",
    unit: p.unit ?? "",
    piItemId: null,
    unitPrice: p.costUnitPrice == null || (currency !== undefined && currency.trim().toUpperCase() !== p.currency.trim().toUpperCase()) ? "" : unitPriceToInput(p.costUnitPrice),
    remarks: "",
    supplierName: supplierName ?? p.supplierName ?? "",
  };
}

export function poLineFromDto(it: PoLineDto): PoLineDraft {
  return {
    id:it.id,
    productId: it.productId ?? "",
    productCode: it.productCode ?? "",
    subRef: it.subRef ?? "",
    lotNumber: it.lotNumber ?? "",
    description: it.description ?? "",
    descriptionZh: it.descriptionZh ?? "",
    color: it.color ?? "",
    size: it.size ?? "",
    moq: it.moq === null ? "" : String(it.moq),
    quantity: String(it.quantity),
    unit: it.unit ?? "",
    piItemId: it.piItemId ?? null,
    unitPrice: unitPriceToInput(it.unitPrice),
    remarks: it.remarks ?? "",
    supplierName: it.supplierName ?? "",
  };
}

type PoLineField = "quantity" | "unitPrice" | "moq";

/**
 * Drafts → API line inputs (minor units). A malformed number is REJECTED with
 * its line and field, never silently turned into 0 — a wrong purchase price is
 * money the company actually pays.
 */
export function poDraftsToInput(
  lines: PoLineDraft[],
): { items: PoLineInput[] } | { error: { line: number; field: PoLineField } } {
  const items: PoLineInput[] = [];
  for (const [i, l] of lines.entries()) {
    // A row with neither a code nor a description is an empty scaffold row.
    if (!l.productCode.trim() && !l.description.trim()) continue;
    const at = (field: PoLineField) => ({ error: { line: i + 1, field } } as const);

    const qty = parseIntField(l.quantity);
    if (qty.invalid || qty.value === undefined || qty.value <= 0) return at("quantity");
    const price = parseMoneyMajor(l.unitPrice);
    if (price.invalid || price.minor === undefined) return at("unitPrice");
    const moq = parseIntField(l.moq);
    if (moq.invalid) return at("moq");

    items.push({
      id:l.id,unitSelection:l.unitSelection,
      productId: l.productId || undefined,
      productCode: l.productCode || undefined,
      subRef: l.subRef || undefined,
      lotNumber: l.lotNumber.trim() || undefined,
      description: l.description || undefined,
      descriptionZh: l.descriptionZh || undefined,
      color: l.color || undefined,
      size: l.size || undefined,
      moq: moq.value,
      quantity: qty.value,
      unit: l.unit.trim() || null,
      piItemId: l.piItemId,
      unitPrice: price.minor ?? 0,
      remarks: l.remarks || undefined,
      supplierName: l.supplierName || undefined,
    });
  }
  return { items };
}

/** A price must be reviewed again when its purchase unit changes. */
export function changePoLineUnit(line: PoLineDraft, unit: string): PoLineDraft {
  return { ...line, unit, unitPrice: unit.trim().toLowerCase() === line.unit.trim().toLowerCase() ? line.unitPrice : "" };
}

/** Live per-line amount, mirroring the server's lineAmountMinor(). */
export function poLineAmount(l: PoLineDraft): number {
  const qty = parseIntField(l.quantity);
  const price = parseMoneyMajor(l.unitPrice);
  if (qty.invalid || price.invalid) return 0;
  return lineAmountMinor(qty.value ?? 0, price.minor ?? 0);
}

/**
 * Subtotal exactly as poService.buildItems() computes it — totalMinor() rounds
 * the sum to 2dp. Exported so the editor page's tax/total preview reuses the
 * same figure instead of re-deriving one that can drift from the server.
 */
export function poLinesSubtotal(lines: PoLineDraft[]): number {
  return totalMinor(lines.reduce((s, l) => s + poLineAmount(l), 0));
}

const inputCls =
  "h-8 w-full rounded-xs border border-line bg-surface px-1.5 text-xs text-ink outline-none focus:border-brand";
/** Ô chỉ đọc (lấy từ danh mục) — căn với chữ trong ô nhập cạnh nó. */
const textCls = "pt-2 text-xs text-ink-soft";

const COLUMN_LABEL: Record<PoColumn, string> = {
  productCode: "common.code", subRef: "common.subRef", description: "common.description", color: "common.color", size: "common.size",
  moq: "common.moq", lotNumber: "poWorkspace.lot", quantity: "common.qty", unit: "common.unit", unitPrice: "common.unitPrice",
  amount: "common.amount", remarks: "common.lineRemark",
};
const RIGHT = new Set<PoColumn>(["moq", "quantity", "unitPrice", "amount"]);
/** Cột mã dính trái khi bảng cuộn ngang; nút xoá dòng dính phải. */
const pinLeft = "sticky left-0 z-10 bg-surface";
const pinRight = "sticky right-0 z-10 bg-surface";

export function PoLinesEditor({
  lines,
  onChange,
  currency = "USD",
  supplierId,
  supplierName,
  readOnly = false,
  lang = "en",
  showSide = true,
}: {
  lines: PoLineDraft[];
  onChange: (lines: PoLineDraft[]) => void;
  currency?: string;
  supplierId: string | null;
  supplierName: string | null;
  readOnly?: boolean;
  /** Ngôn ngữ in của chính đơn mua này — quyết định hiện bản mô tả nào. */
  lang?: DescriptionLang;
  /** Hiện cột phụ (ghi chú dòng). Trang tính: có dòng dùng thì luôn hiện, không thì theo nút "Hiện cột phụ". */
  showSide?: boolean;
}) {
  const { t } = useTranslation();
  const set = (i: number, patch: Partial<PoLineDraft>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const remove = (i: number) => onChange(lines.filter((_, j) => j !== i));
  const pickedIds = new Set(lines.map((l) => l.productId).filter(Boolean));
  // Nút "Đơn vị & giá" chỉ ở dòng mà mã có hồ sơ quy đổi đã duyệt (19/09/2026 — chốt UOM-R01..R03).
  const activeUnits = useActiveUnitProducts(lines.map((l) => l.productId));
  const columns: PoColumn[] = [...PO_MAIN_COLUMNS, ...(showSide ? PO_SIDE_COLUMNS : [])];
  /*
   * Độ rộng CỐ ĐỊNH từng cột (table-fixed, 19/09/2026 — cùng chuẩn bảng PI và báo giá), cột mô tả lấy phần còn lại và xuống dòng.
   * Trước đây bảng tự giãn, mô tả bị cắt ở 16rem ("Brass Cap Rivet w…") dù màn còn chỗ.
   */
  const minRem = PO_EDGE_COLUMNS_REM + columns.reduce((sum, c) => sum + (PO_COLUMN_WIDTH[c] ?? PO_DESCRIPTION_MIN_REM), 0);

  const cell = (l: PoLineDraft, i: number, c: PoColumn) => {
    const aria = `${t(COLUMN_LABEL[c])} ${i + 1}`;
    switch (c) {
      case "productCode":
        return (
          <>
            <p className={`${textCls} font-mono tabular text-ink`}>{l.productCode || "—"}</p>
            {l.productId && activeUnits.has(l.productId) && (
              <UnitPriceChoice key={`${l.productId}:${currency}`} productId={l.productId} currency={currency} unit={l.unit} quantity={l.quantity}
                unitPrice={l.unitPrice} purchase disabled={readOnly || !!l.piItemId} onApply={(choice) => set(i, choice)} />
            )}
          </>
        );
      case "subRef":
        return <p className={`${textCls} font-mono tabular`}>{l.subRef || "—"}</p>;
      case "description":
        return (
          <div className={textCls}>
            <span className="block text-ink">{describe(l, lang) || "—"}</span>
            {l.supplierName && <span className="block text-2xs text-ink-faint">{l.supplierName}</span>}
          </div>
        );
      case "color":
        return <p className={textCls}>{l.color || "—"}</p>;
      case "size":
        return <p className={textCls}>{l.size || "—"}</p>;
      case "moq":
        return <p className={`${textCls} text-right font-mono tabular`}>{l.moq ? formatQty(Number(l.moq)) : "—"}</p>;
      case "lotNumber":
        return (
          <input className={`${inputCls} font-mono tabular`} disabled={readOnly} aria-label={aria} maxLength={120} value={l.lotNumber}
            title={l.lotNumber || undefined} onChange={(e) => set(i, { lotNumber: e.target.value })} />
        );
      case "quantity":
        return (
          <input className={`${inputCls} text-right font-mono tabular`} inputMode="numeric" disabled={readOnly} aria-label={aria}
            aria-invalid={parseIntField(l.quantity).invalid ? true : undefined} value={l.quantity}
            onChange={(e) => set(i, { quantity: e.target.value })} />
        );
      case "unit":
        return (
          <input className={inputCls} disabled={readOnly} aria-label={aria} maxLength={40} value={l.unit}
            onChange={(e) => set(i, changePoLineUnit(l, e.target.value))} />
        );
      case "unitPrice":
        return (
          <input className={`${inputCls} text-right font-mono tabular`} inputMode="decimal" disabled={readOnly} aria-label={aria}
            aria-invalid={!l.unitPrice.trim() || parseMoneyMajor(l.unitPrice).invalid ? true : undefined}
            placeholder={t("poWorkspace.priceRequired")} title={t("poWorkspace.priceRequired")} value={l.unitPrice}
            onChange={(e) => set(i, { unitPrice: e.target.value })} />
        );
      case "amount":
        return <p className="pt-2 text-right font-mono tabular text-xs text-ink">{formatMoney(poLineAmount(l), currency)}</p>;
      case "remarks":
        return (
          <input className={inputCls} disabled={readOnly} aria-label={aria} value={l.remarks} title={l.remarks || undefined}
            onChange={(e) => set(i, { remarks: e.target.value })} />
        );
    }
  };

  return (
    <div className="bg-surface">
      <div className="overflow-x-auto" tabIndex={0}>
        <table className="w-full table-fixed border-collapse text-sm" style={{ minWidth: `${minRem}rem` }}>
          <colgroup>
            <col style={{ width: "2rem" }} />
            {columns.map((c) => {
              const w = PO_COLUMN_WIDTH[c];
              return <col key={c} style={w ? { width: `${w}rem` } : undefined} />;
            })}
            <col style={{ width: "2rem" }} />
          </colgroup>
          <thead>
            <tr className="[&>th]:border-b [&>th]:border-line [&>th]:bg-sunken/60 [&>th]:px-1.5 [&>th]:py-2 [&>th]:align-bottom [&>th]:text-2xs [&>th]:font-semibold [&>th]:uppercase [&>th]:leading-tight [&>th]:tracking-wide [&>th]:text-ink-faint">
              <th className="text-right">#</th>
              {columns.map((c) => (
                <th key={c} className={`${RIGHT.has(c) ? "text-right" : "text-left"} ${c === "productCode" ? `${pinLeft} bg-sunken` : ""}`}>
                  {c === "description" ? `${t(COLUMN_LABEL[c])} · ${t(`common.printLanguage_${lang}`)}` : t(COLUMN_LABEL[c])}
                </th>
              ))}
              <th className={`${pinRight} bg-sunken`}><span className="sr-only">{t("common.removeLine")}</span></th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td colSpan={columns.length + 2} className="px-2 py-6 text-center text-xs text-ink-faint">
                  {t("purchaseOrders.noItems")}
                </td>
              </tr>
            )}
            {lines.map((l, i) => (
              <tr key={i} className="[&>td]:border-b [&>td]:border-line [&>td]:px-1.5 [&>td]:py-1.5 [&>td]:align-top">
                <td className="pt-3.5 text-right font-mono text-2xs text-ink-faint">{String(i + 1).padStart(2, "0")}</td>
                {columns.map((c) => (
                  <td key={c} className={c === "productCode" ? pinLeft : undefined}>{cell(l, i, c)}</td>
                ))}
                <td className={`pt-3 text-center ${pinRight}`}>
                  {!readOnly && (
                    <button type="button" onClick={() => remove(i)} aria-label={`${t("common.removeLine")} ${i + 1}`} className="text-ink-faint hover:text-danger">
                      <Trash2 className="size-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div className="flex flex-wrap items-start gap-2 px-3 py-2">
          <PoProductPicker
            supplierId={supplierId}
            supplierName={supplierName}
            disabledIds={pickedIds}
            onPick={(p) => onChange([...lines, poLineFromProduct(p, supplierName, currency)])}
          />
        </div>
      )}
    </div>
  );
}
